import type { Class, GroupReportItem, StudentReportItem } from '@/school-management/types/response';
import type { GroupTeacher } from '@/school-management/types/response/teacher';
import { LEVEL_CONTROL_DEFAULTS } from '@/school-management/utils/levelControl';
import type { ClassProgress } from '@/types/progress';
import { UserStatusEnum } from '@/types/register';
import type { UserState } from '@/types/user';

/** API may expose extra fields on the overview payload. */
type ClassProgressExtended = ClassProgress & {
  groupName?: string;
  groupId?: string;
  classCode?: string;
  gradeId?: string | number | null;
  groupDetails?: ClassProgress['groupDetails'] & {
    groupName?: string;
    groupId?: string;
    classCode?: string;
    gradeId?: string | number | null;
    placementTestRequired?: boolean;
    defaultStartingLevel?: string | number | null;
    allowIndividualAdvancement?: boolean;
  };
  placementTestRequired?: boolean;
  defaultStartingLevel?: string | number | null;
  allowIndividualAdvancement?: boolean;
};

export function parseTeacherOverviewResponse(raw: unknown): ClassProgress | undefined {
  return (raw as { data?: { data?: ClassProgress } })?.data?.data;
}

/**
 * Builds admin {@link Class} shape from Teacher-view `my-class-overview` body so shared UI can render
 * without `admin-management/.../groups/details`.
 */
export function buildClassFromTeacherOverview(
  cp: ClassProgress | undefined,
  classId: string,
  user: UserState | null,
  fallbackTeacherName?: string
): Class {
  const students = cp?.studentsInGroup ?? [];
  const ext = cp as ClassProgressExtended | undefined;

  const activeStudentsCount = students.filter(
    s =>
      s.userStatus === UserStatusEnum.ACTIVE ||
      s.userStatus === UserStatusEnum.LEVEL_UP ||
      s.userStatus === UserStatusEnum.LEVEL_DOWN ||
      s.userStatus === UserStatusEnum.LEVEL_COMPLETED_BLOCKED
  ).length;

  const levelTestIncompleteStudentsCount = students.filter(
    s => s.userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE
  ).length;

  const resetPasswordStudentsCount = students.filter(
    s => s.userStatus === UserStatusEnum.RESET_PASSWORD
  ).length;

  const teachers: GroupTeacher[] = [];
  if (user?.metadata?.id && user.metadata.firstName) {
    teachers.push({
      id: user.metadata.id,
      firstName: user.metadata.firstName,
      lastName: user.metadata.lastName || '',
    });
  } else if (fallbackTeacherName?.trim()) {
    const parts = fallbackTeacherName.trim().split(/\s+/);
    teachers.push({
      id: 'current-teacher',
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    });
  }

  const co = cp?.groupDetails?.additionalTeacherName;
  if (co) {
    teachers.push({ id: co.id, firstName: co.firstName, lastName: co.lastName });
  }

  const groupId = (ext?.groupDetails?.groupId || ext?.groupId || '').trim() || classId;
  const groupName = (ext?.groupDetails?.groupName || ext?.groupName || '').trim();
  const gradeId = ext?.groupDetails?.gradeId ?? ext?.gradeId;

  return {
    groupId,
    groupName,
    teachers,
    grade: '',
    gradeId,
    studentCount: students.length,
    activeStudentsCount,
    levelTestIncompleteStudentsCount,
    resetPasswordStudentsCount,
    classCode: ext?.groupDetails?.classCode || ext?.classCode,
    placementTestRequired:
      cp?.groupDetails?.groupSettings?.placementTestRequired ??
      ext?.placementTestRequired ??
      LEVEL_CONTROL_DEFAULTS.placementTestRequired,
    defaultStartingLevel:
      cp?.groupDetails?.groupSettings?.defaultStartingLevel ?? ext?.defaultStartingLevel,
    allowIndividualAdvancement:
      cp?.groupDetails?.groupSettings?.allowIndividualAdvancement ??
      ext?.allowIndividualAdvancement ??
      LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
    lastUpdateAt: cp?.groupDetails?.lastUpdateAt,
  };
}

export function buildClassFromGroupReportItem(item: GroupReportItem, routeClassId: string): Class {
  const groupId = (item.groupId || routeClassId).trim() || routeClassId;
  return {
    groupId,
    groupName: (item.groupName || '').trim(),
    teachers: (item.teachers || []).map(t => ({
      id: t.userId,
      firstName: t.firstName,
      lastName: t.lastName,
    })),
    grade: '',
    gradeId: item.gradeId,
    studentCount: item.numberOfStudents,
    activeStudentsCount: item.numberOfActiveStudents,
    levelTestIncompleteStudentsCount: 0,
    resetPasswordStudentsCount: 0,
    classCode: item.classCode ?? undefined,
  };
}

function teachersFallback(user: UserState | null, fallbackTeacherName?: string): GroupTeacher[] {
  if (user?.metadata?.id && user.metadata.firstName) {
    return [
      {
        id: user.metadata.id,
        firstName: user.metadata.firstName,
        lastName: user.metadata.lastName || '',
      },
    ];
  }
  if (fallbackTeacherName?.trim()) {
    const parts = fallbackTeacherName.trim().split(/\s+/);
    return [
      {
        id: 'current-teacher',
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
      },
    ];
  }
  return [];
}

/** When `groups/details` is unavailable; build from students report (same search API as the table). */
export function buildClassFromStudentReportItems(
  routeClassId: string,
  items: StudentReportItem[],
  user: UserState | null,
  fallbackTeacherName?: string
): Class {
  if (items.length === 0) {
    return {
      groupId: routeClassId,
      groupName: '',
      teachers: teachersFallback(user, fallbackTeacherName),
      grade: '',
      studentCount: 0,
      activeStudentsCount: 0,
      levelTestIncompleteStudentsCount: 0,
      resetPasswordStudentsCount: 0,
    };
  }

  const row0 = items[0];
  const groupId = (row0.groupId || routeClassId).trim() || routeClassId;
  const groupName = (row0.groupName || '').trim();

  const teacherById = new Map<string, GroupTeacher>();
  for (const row of items) {
    for (const te of row.teachers || []) {
      if (te.userId && !teacherById.has(te.userId)) {
        teacherById.set(te.userId, {
          id: te.userId,
          firstName: te.firstName,
          lastName: te.lastName,
        });
      }
    }
  }
  const teachersFromRows = [...teacherById.values()];
  const teachers =
    teachersFromRows.length > 0 ? teachersFromRows : teachersFallback(user, fallbackTeacherName);

  const activeStudentsCount = items.filter(
    s =>
      s.userStatus === UserStatusEnum.ACTIVE ||
      s.userStatus === UserStatusEnum.LEVEL_UP ||
      s.userStatus === UserStatusEnum.LEVEL_DOWN ||
      s.userStatus === UserStatusEnum.LEVEL_COMPLETED_BLOCKED
  ).length;
  const levelTestIncompleteStudentsCount = items.filter(
    s => s.userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE
  ).length;
  const resetPasswordStudentsCount = items.filter(
    s => s.userStatus === UserStatusEnum.RESET_PASSWORD
  ).length;

  return {
    groupId,
    groupName,
    teachers,
    grade: '',
    gradeId: row0.gradeId,
    studentCount: items.length,
    activeStudentsCount,
    levelTestIncompleteStudentsCount,
    resetPasswordStudentsCount,
  };
}
